import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChart2, ArrowLeft, ShieldCheck, User, LogOut, Search, Layers, FileText, Gift, Crown } from 'lucide-react';
import { useRyanAuth } from '../context/RyanAuthContext';
import { useAuth } from '../../contexts/AuthContext';
import './RyanHeader.css';

/**
 * Ilustrações decorativas exclusivas do RyanHeader (modo home) — traço branco
 * translúcido sobre o verde do header, motivos próprios (sem reaproveitar
 * RyanFieldPattern.tsx nem HeaderPattern.tsx): lado direito com pulso de EKG,
 * cruz de saúde, cápsula e círculos; lado esquerdo com hospital, jaleco e
 * estetoscópio.
 */
const RyanHeaderHomePatternRight: React.FC = () => (
  <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice">
    <g fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <g className="ryan-hp-circles">
        <circle cx="335" cy="55" r="54" opacity="0.07" />
        <circle cx="335" cy="55" r="36" opacity="0.1" />
        <circle cx="335" cy="55" r="17" opacity="0.14" />
      </g>
      <path className="ryan-hp-ekg" d="M30 195 h48 l14 -36 l20 62 l16 -84 l14 58 h56" opacity="0.17" />
      <path className="ryan-hp-cross" d="M300 235 v42 M279 256 h42" strokeWidth={3.5} opacity="0.19" />
      {/* cápsula */}
      <g className="ryan-hp-capsule">
        <rect x="230" y="55" width="68" height="28" rx="14" opacity="0.1" transform="rotate(-25 264 69)" />
        <line x1="251" y1="53" x2="251" y2="85" opacity="0.13" transform="rotate(-25 264 69)" />
      </g>
      <g className="ryan-hp-dots">
        <circle cx="70" cy="95" r="4" opacity="0.16" />
        <circle cx="98" cy="72" r="3" opacity="0.12" />
        <circle cx="250" cy="270" r="2.5" opacity="0.14" />
        <circle cx="368" cy="185" r="3" opacity="0.1" />
      </g>
    </g>
  </svg>
);

const RyanHeaderHomePatternLeft: React.FC = () => (
  <svg viewBox="0 0 570 300" preserveAspectRatio="xMidYMid slice">
    <g fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      {/* hospital — deslocado 3cm (~114 unidades) à direita a pedido do usuário */}
      <g className="ryan-hp-hospital" transform="translate(114 0)">
        <rect x="190" y="150" width="90" height="90" opacity="0.08" />
        <path d="M223 145 h14 v-14 h14 v14 h14 v14 h-14 v14 h-14 v-14 h-14 Z" opacity="0.13" />
        <line x1="205" y1="175" x2="217" y2="175" opacity="0.1" />
        <line x1="253" y1="175" x2="265" y2="175" opacity="0.1" />
        <line x1="205" y1="205" x2="217" y2="205" opacity="0.09" />
        <line x1="253" y1="205" x2="265" y2="205" opacity="0.09" />
      </g>
      {/* jaleco — corpo reto (sem afunilar na lateral, como um jaleco real)
          com mangas compridas nos dois ombros — deslocado 2cm (~76 unidades)
          à direita a pedido do usuário */}
      <g className="ryan-hp-jaleco" transform="translate(76 0)">
        <path
          d="M416 132 L430 148 L444 132 L450 138 L450 224 L410 224 L410 138 Z"
          opacity="0.16"
        />
        <rect x="450" y="140" width="15" height="58" rx="5" opacity="0.14" transform="rotate(13 457.5 140)" />
        <rect x="395" y="140" width="15" height="58" rx="5" opacity="0.14" transform="rotate(-13 402.5 140)" />
        <line x1="430" y1="150" x2="430" y2="222" opacity="0.13" />
        <circle cx="430" cy="168" r="1.6" opacity="0.15" />
        <circle cx="430" cy="186" r="1.6" opacity="0.15" />
        <circle cx="430" cy="204" r="1.6" opacity="0.15" />
      </g>
      {/* estetoscópio — o "laço" (tubo curvo) mencionado pelo usuário */}
      <g className="ryan-hp-stetho">
        <path d="M55 40 q0 34 22 34 q22 0 22 -25" opacity="0.09" />
        <circle cx="99" cy="49" r="6" opacity="0.11" />
        <circle cx="55" cy="35" r="3" opacity="0.08" />
      </g>
    </g>
  </svg>
);

/* Bastão de Asclépio — cobra enrolada em um bastão, símbolo da medicina.
 * Corpo em curvas suaves (cubic bezier) formando espirais reais, cabeça em
 * losango alongado (focinho + queixo definidos, não uma gota genérica), com
 * dois olhos e língua bífida — silhueta bem mais reconhecível como cobra. */
const RyanHeaderAsclepiusPattern: React.FC = () => (
  <svg viewBox="0 0 160 200" preserveAspectRatio="xMidYMid meet">
    <g fill="none" stroke="#fff" strokeLinecap="round" strokeLinejoin="round">
      <line x1="80" y1="40" x2="80" y2="184" strokeWidth={3} opacity="0.19" />
      <path
        d="M80 44 C 102 52, 103 76, 80 86 C 57 96, 56 120, 80 130 C 101 138, 102 158, 80 168"
        strokeWidth={2.5}
        opacity="0.21"
      />
      {/* cabeça — losango alongado, focinho apontando pra cima */}
      <path
        d="M80 15 L89 27 L84 38 L80 41 L76 38 L71 27 Z"
        fill="#fff"
        stroke="none"
        opacity="0.32"
      />
      <circle cx="75.5" cy="25" r="1.3" fill="#fff" stroke="none" opacity="0.45" />
      <circle cx="84.5" cy="25" r="1.3" fill="#fff" stroke="none" opacity="0.45" />
      <path d="M80 15 l-4 -7 M80 15 l4 -7" strokeWidth={1.5} opacity="0.18" />
    </g>
  </svg>
);

interface RyanSearchDeckResult {
  deckId: string;
  subdeckId: string;
  name: string;
  type: 'flashcards' | 'apostila';
  spaceId: string;
  spaceName: string;
  tabId: string | null;
}

interface RyanHeaderProps {
  showLogout?: boolean;
  title?: string;
  subtitle?: string;
  onBack?: () => void;
  logoInCenter?: boolean;
  rightExtra?: React.ReactNode;
  /** Repassa o texto digitado na busca do header pra quem precisar reagir
      (RyanHomePage usa isso pra trazer o campo pesquisado pra frente na grade). */
  onSearchQueryChange?: (query: string) => void;
  /** Classe extra no <header> — pra páginas que precisam de um visual próprio
      (ex: gradiente mais rico) sem alterar o fundo padrão do header em todas
      as outras páginas que reaproveitam este componente. */
  className?: string;
}

const RyanHeader: React.FC<RyanHeaderProps> = ({
  showLogout = false,
  title,
  subtitle,
  onBack,
  logoInCenter = false,
  rightExtra,
  onSearchQueryChange,
  className,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const { isVip, isEditor, isRepresentative } = useRyanAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDecks, setSearchDecks] = useState<RyanSearchDeckResult[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResultsPos, setSearchResultsPos] = useState({ top: 0, left: 0, width: 0 });
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  // Avatar do modo "home" abre o dropdown via portal em document.body — o
  // header usa overflow:hidden pra recortar as ilustrações de fundo, o que
  // também cortaria o dropdown se ele ficasse na árvore normal (mesmo padrão
  // de portal já usado em RyanFieldHero/RyanHomePage).
  const profileBtnRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/ryan/login');
  };

  // Busca real (campos, decks e apostilas que o usuário tem acesso) —
  // debounced pra não disparar uma requisição a cada tecla.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchDecks([]);
      setSearchOpen(false);
      return;
    }
    const token = localStorage.getItem('accessToken');
    const timer = setTimeout(() => {
      fetch(`/api/ryan/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => (r.ok ? r.json() : null))
        .then(data => {
          setSearchDecks(data?.decks || []);
          if (searchWrapRef.current) {
            const rect = searchWrapRef.current.getBoundingClientRect();
            setSearchResultsPos({
              top: rect.bottom + window.scrollY + 8,
              left: rect.left + window.scrollX,
              width: rect.width,
            });
          }
          setSearchOpen(true);
        })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (searchWrapRef.current?.contains(target) || searchResultsRef.current?.contains(target)) return;
      setSearchOpen(false);
    };
    if (searchOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [searchOpen]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    onSearchQueryChange?.(value);
  };

  const handleSearchResultClick = (deck: RyanSearchDeckResult) => {
    setSearchOpen(false);
    setSearchQuery('');
    onSearchQueryChange?.('');
    navigate(`/ryan/campo/${deck.spaceId}`, {
      state: { highlightSubdeckId: deck.subdeckId, highlightTabId: deck.tabId },
    });
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dropdownRef.current?.contains(target) || profileBtnRef.current?.contains(target)) return;
      setDropdownOpen(false);
    };
    if (dropdownOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  const toggleHomeDropdown = () => {
    if (!dropdownOpen && profileBtnRef.current) {
      const rect = profileBtnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + window.scrollY + 10, left: rect.right + window.scrollX - 160 });
    }
    setDropdownOpen(v => !v);
  };

  const username = user?.username || '';
  const firstName = username.split(' ')[0] || username;
  const profilePhoto = user?.profilePhoto || null;

  const getGreeting = () => {
    if (isAdmin) {
      return { name: 'chefe', complement: 'O que vamos construir hoje?', showVip: false };
    }
    if (isVip && !isEditor) {
      return { name: firstName, complement: 'Bom estudo hoje!', showVip: true };
    }
    return { name: firstName, complement: 'O que vamos estudar hoje?', showVip: false };
  };

  const greeting = getGreeting();
  const userInitial = (username || '?')[0].toUpperCase();

  // Modo "home" — 3 colunas (marca/busca/avatar) + saudação abaixo. Só se aplica
  // quando o header não está em modo sub-página (onBack/title), ex: RyanHomePage.
  const isHomeMode = showLogout && !onBack && !title;

  if (isHomeMode) {
    return (
      <header className="ryan-header">
        <div className="ryan-header-home">
          <div className="ryan-header-home-pattern ryan-header-home-pattern--left">
            <RyanHeaderHomePatternLeft />
          </div>
          <div className="ryan-header-home-pattern ryan-header-home-pattern--right">
            <RyanHeaderHomePatternRight />
          </div>
          <div className="ryan-header-home-pattern ryan-header-home-pattern--center">
            <RyanHeaderAsclepiusPattern />
          </div>

          <div className="ryan-header-home-inner">
            <div className="ryan-header-home-row">
              <div className="ryan-header-brand">
                <img src="/Ryan/graia-logo-branca.png" alt="Graia" className="ryan-header-logo ryan-header-logo--brand" />
                <div className="ryan-header-wordmark">
                  <span className="ryan-header-wordmark-graia">GRAIA</span>
                  <span className="ryan-header-wordmark-medicina">medicina</span>
                </div>
              </div>

              <div className="ryan-header-greeting ryan-header-greeting--center">
                <div className="ryan-header-greeting-top">
                  <span className="ryan-header-greeting-name">
                    Olá, {greeting.name}!
                  </span>
                  {greeting.showVip && (
                    <span className="ryan-header-vip-badge">
                      <Crown size={10} strokeWidth={2.5} />
                      VIP
                    </span>
                  )}
                </div>
                <span className="ryan-header-greeting-sub">{greeting.complement}</span>
              </div>

              <div className="ryan-header-home-actions">
                <div className="ryan-header-profile-wrap ryan-header-profile-wrap--lg">
                  <button
                    ref={profileBtnRef}
                    className="ryan-header-profile-btn ryan-header-profile-btn--lg"
                    onClick={toggleHomeDropdown}
                    title="Meu perfil"
                  >
                    {profilePhoto ? (
                      <img
                        src={profilePhoto}
                        alt="Foto de perfil"
                        className="ryan-header-profile-photo"
                      />
                    ) : (
                      <span className="ryan-header-profile-initial">{userInitial}</span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="ryan-header-search-bar ryan-header-search-bar--wide" ref={searchWrapRef}>
              <Search size={15} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                onFocus={() => { if (searchDecks.length > 0) setSearchOpen(true); }}
                placeholder="Buscar campo, deck ou apostila…"
              />
            </div>
          </div>

          {dropdownOpen && ReactDOM.createPortal(
            <div
              ref={dropdownRef}
              className="ryan-header-dropdown"
              style={{ position: 'absolute', top: dropdownPos.top, left: dropdownPos.left }}
            >
              <button
                className="ryan-header-dropdown-item"
                onClick={() => { setDropdownOpen(false); navigate('/ryan/minha-area'); }}
              >
                <User size={15} />
                Área do Aluno
              </button>
              {isRepresentative && (
                <button
                  className="ryan-header-dropdown-item"
                  onClick={() => { setDropdownOpen(false); navigate('/ryan/embaixador'); }}
                >
                  <Gift size={15} />
                  Painel do Embaixador
                </button>
              )}
              {isAdmin && (
                <button
                  className="ryan-header-dropdown-item"
                  onClick={() => { setDropdownOpen(false); navigate('/ryan/admin'); }}
                >
                  <ShieldCheck size={15} />
                  Painel Admin
                </button>
              )}
              <div className="ryan-header-dropdown-divider" />
              <button
                className="ryan-header-dropdown-item ryan-header-dropdown-item--danger"
                onClick={() => { setDropdownOpen(false); handleLogout(); }}
              >
                <LogOut size={15} />
                Sair
              </button>
            </div>,
            document.body
          )}

          {searchOpen && ReactDOM.createPortal(
            <div
              ref={searchResultsRef}
              className="ryan-header-search-results"
              style={{ position: 'absolute', top: searchResultsPos.top, left: searchResultsPos.left, width: searchResultsPos.width }}
            >
              {searchDecks.length === 0 ? (
                <div className="ryan-header-search-empty">Nenhum deck ou apostila encontrado</div>
              ) : (
                searchDecks.map(d => (
                  <button
                    key={`${d.deckId}-${d.subdeckId}`}
                    className="ryan-header-search-item"
                    onMouseDown={e => { e.preventDefault(); handleSearchResultClick(d); }}
                  >
                    <span className="ryan-header-search-item-icon">
                      {d.type === 'apostila' ? <FileText size={14} /> : <Layers size={14} />}
                    </span>
                    <span className="ryan-header-search-item-text">
                      <span className="ryan-header-search-item-name">{d.name}</span>
                      <span className="ryan-header-search-item-meta">
                        {d.type === 'apostila' ? 'Apostila' : 'Deck'} · está em {d.spaceName}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>,
            document.body
          )}
        </div>
      </header>
    );
  }

  return (
    <header className={`ryan-header${className ? ` ${className}` : ''}`}>
      <div className="ryan-header-default">
        <div className="ryan-header-left">
          {onBack ? (
            <button className="ryan-header-back-btn" onClick={onBack} title="Voltar">
              <ArrowLeft size={18} />
            </button>
          ) : (
            <img src="/Ryan/graia-logo-branca.png" alt="Graia" className="ryan-header-logo" />
          )}
        </div>

        {title && (
          <div className="ryan-header-center">
            {logoInCenter && (
              <>
                <img
                  src="/Ryan/graia-logo-branca.png"
                  alt="Graia"
                  className="ryan-header-logo ryan-header-center-logo"
                />
                <div className="ryan-header-center-divider" />
              </>
            )}
            <div className="ryan-header-center-text">
              <span className="ryan-header-title">{title}</span>
              {subtitle && <span className="ryan-header-subtitle">{subtitle}</span>}
            </div>
          </div>
        )}

        {(showLogout || rightExtra) && (
          <div className="ryan-header-right">
            {rightExtra}

            {showLogout && (
              <>
                {isAdmin && !rightExtra && (
                  <button
                    className="ryan-home-admin-header-btn"
                    onClick={() => navigate('/ryan/admin')}
                    title="Administração"
                  >
                    <ShieldCheck size={16} />
                    Admin
                  </button>
                )}

                <button
                  className="ryan-header-stats-btn"
                  onClick={() => navigate('/ryan/estatisticas', {
                    state: { returnPath: location.pathname }
                  })}
                  title="Estatísticas"
                >
                  <BarChart2 size={18} />
                </button>

                <div className="ryan-header-profile-wrap" ref={dropdownRef}>
                  <button
                    className="ryan-header-profile-btn"
                    onClick={() => setDropdownOpen(v => !v)}
                    title="Meu perfil"
                  >
                    {profilePhoto ? (
                      <img
                        src={profilePhoto}
                        alt="Foto de perfil"
                        className="ryan-header-profile-photo"
                      />
                    ) : (
                      <span className="ryan-header-profile-initial">{userInitial}</span>
                    )}
                  </button>

                  {dropdownOpen && (
                    <div className="ryan-header-dropdown">
                      <button
                        className="ryan-header-dropdown-item"
                        onClick={() => { setDropdownOpen(false); navigate('/ryan/minha-area'); }}
                      >
                        <User size={15} />
                        Área do Aluno
                      </button>
                      {isRepresentative && (
                        <button
                          className="ryan-header-dropdown-item"
                          onClick={() => { setDropdownOpen(false); navigate('/ryan/embaixador'); }}
                        >
                          <Gift size={15} />
                          Painel do Embaixador
                        </button>
                      )}
                      <div className="ryan-header-dropdown-divider" />
                      <button
                        className="ryan-header-dropdown-item ryan-header-dropdown-item--danger"
                        onClick={() => { setDropdownOpen(false); handleLogout(); }}
                      >
                        <LogOut size={15} />
                        Sair
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default RyanHeader;
